import {HttpClient, HttpHeaders} from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { AppConfiguration, AppConfigurationProvider } from '@app-core/models/config';
import { IDecryptResponse } from '@app-core/models/decrypt-response';
import { IValidationResponse } from '@app-core/models/validation-response';
import { CardDetails } from '@app-pages/models/card-details';
import { environment } from '@environments/environment';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import * as Sentry from "@sentry/angular";

/* eslint-disable @typescript-eslint/no-explicit-any */

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private _apiUrl: string;
  private _isMock = !environment.production && environment.api.mock;
  private _fileExtension = this._isMock ? '.json' : '';

  constructor(
    private _http: HttpClient,
    @Inject(AppConfigurationProvider) private _config: AppConfiguration
  ) {
    this._apiUrl = this._isMock ? './assets/mock-data/pasfoto-kaartvernieuwing' : this._config.api_url;
    const overrideApiUrl = sessionStorage.getItem('pasfoto-kaartvernieuwing-api-url');
    if (overrideApiUrl) {
      this._apiUrl = overrideApiUrl;
    }
  }

  public validateCardDetails(cardDetails: CardDetails): Observable<IValidationResponse> {
    const url = `${this._apiUrl}/${this._config.api_version}/validate${
      this._fileExtension
    }`;
    if (this._isMock) return this._http.get<IValidationResponse>(url);

    return this._http.post<IValidationResponse>(url, cardDetails, this.addSubscriptionKey()).pipe(
      catchError(error => {
        Sentry.setContext("cardDetails", cardDetails);
        Sentry.captureException(error.message ? error.message : error);
        return of({ succes: false, error: { message: 'GENERAL.ERROR' } });
      })
    );
  }

  public confirmCardDetails(cardDetails: CardDetails): Observable<IValidationResponse> {
    const url = `${this._apiUrl}/${this._config.api_version}/confirm${
      this._fileExtension
    }`;
    if (this._isMock) return this._http.get<IValidationResponse>(url);

    return this._http.post<IValidationResponse>(url, cardDetails, this.addSubscriptionKey()).pipe(
      catchError(error => {
        Sentry.setContext("cardDetails", cardDetails);
        Sentry.captureException(error.message ? error.message : error);
        return of({ succes: false, error: { message: 'GENERAL.ERROR' } });
      })
    );
  }

  public decryptToken(token: string): Observable<IDecryptResponse> {
    const url = `${this._apiUrl}/${this._config.api_version}/decrypt${
      this._fileExtension
    }`;
    if (this._isMock) return this._http.get<IDecryptResponse>(url);

    return this._http.post<IDecryptResponse>(url, token, this.addSubscriptionKey()).pipe(
      catchError(error => {
        Sentry.setContext("token", {token});
        Sentry.captureException(error.message ? error.message : error);
        return of({ succes: false, error: { message: 'GENERAL.ERROR' } });
      })
    );
  }

  private addSubscriptionKey(customOptions?: {}): {} {
    if (this._config.api_subscription_key && this._config.api_subscription_key.length > 0) {
      return {
        headers: new HttpHeaders({'Ocp-Apim-Subscription-Key': this._config.api_subscription_key}),
        ...customOptions,
      };
    } else {
      return {...customOptions};
    }
  }
}
